import type { Member } from "./contracts";

export type ArtistData = {
  display: string;
  songs: string[];
  albums: string[];
  lore: string[];
};

export type QuestionType = "opinion" | "receipt";

export type ArtistQuestion = {
  text: string;
  type: QuestionType;
};

export const artistKnowledge: Record<string, ArtistData> = {
  beyonce: {
    display: "Beyonce",
    songs: ["Crazy in Love", "Formation", "Love on Top", "Virgo's Groove"],
    albums: ["Dangerously in Love", "Lemonade", "Renaissance", "Cowboy Carter"],
    lore: ["the surprise self-titled drop", "the Coachella/Homecoming era", "the Renaissance ballroom references"],
  },
  "beyonce knowles": {
    display: "Beyonce",
    songs: ["Crazy in Love", "Formation", "Love on Top", "Virgo's Groove"],
    albums: ["Dangerously in Love", "Lemonade", "Renaissance", "Cowboy Carter"],
    lore: ["the surprise self-titled drop", "the Coachella/Homecoming era", "the Renaissance ballroom references"],
  },
  radiohead: {
    display: "Radiohead",
    songs: ["Paranoid Android", "Everything in Its Right Place", "Idioteque", "True Love Waits"],
    albums: ["OK Computer", "Kid A", "In Rainbows", "A Moon Shaped Pool"],
    lore: ["the pay-what-you-want In Rainbows release", "the OK Computer paranoia", "the Kid A left turn"],
  },
  mitski: {
    display: "Mitski",
    songs: ["Your Best American Girl", "Nobody", "First Love/Late Spring", "Washing Machine Heart"],
    albums: ["Bury Me at Makeout Creek", "Puberty 2", "Be the Cowboy", "Laurel Hell"],
    lore: ["the Be the Cowboy choreography", "the Puberty 2 emotional whiplash", "the Laurel Hell synth era"],
  },
  bts: {
    display: "BTS",
    songs: ["Spring Day", "Blood Sweat & Tears", "Dynamite", "Black Swan"],
    albums: ["Wings", "Love Yourself: Tear", "Map of the Soul: 7", "BE"],
    lore: ["the BU theories", "the Love Yourself era", "the Wings short films"],
  },
  "taylor swift": {
    display: "Taylor Swift",
    songs: ["All Too Well", "Cruel Summer", "Style", "cardigan"],
    albums: ["Red", "1989", "folklore", "Midnights"],
    lore: ["the vault tracks", "the easter egg theories", "the Eras Tour surprise songs"],
  },
  "kendrick lamar": {
    display: "Kendrick Lamar",
    songs: ["Alright", "DNA.", "Sing About Me, I'm Dying of Thirst", "Not Like Us"],
    albums: ["good kid, m.A.A.d city", "To Pimp a Butterfly", "DAMN.", "Mr. Morale & the Big Steppers"],
    lore: ["the Compton narrative", "the DAMN. reverse-order theory", "the Pulitzer discourse"],
  },
  "lana del rey": {
    display: "Lana Del Rey",
    songs: ["Video Games", "Venice Bitch", "A&W", "Ride"],
    albums: ["Born to Die", "Norman Fucking Rockwell!", "Ultraviolence", "Did you know that there's a tunnel under Ocean Blvd"],
    lore: ["the Lizzy Grant mythos", "the NFR critical turn", "the long song titles era"],
  },
  "frank ocean": {
    display: "Frank Ocean",
    songs: ["Thinkin Bout You", "Nights", "Pyramids", "Self Control"],
    albums: ["Channel Orange", "Blonde", "Endless", "Nostalgia, Ultra"],
    lore: ["the Blonde/Endless release week", "the wait between albums", "the Tumblr-era fan archaeology"],
  },
  nirvana: {
    display: "Nirvana",
    songs: ["Smells Like Teen Spirit", "Lithium", "Heart-Shaped Box", "Drain You"],
    albums: ["Nevermind", "In Utero", "Bleach", "MTV Unplugged in New York"],
    lore: ["the Unplugged performance", "the Albini production debate", "the Sub Pop beginnings"],
  },
  weezer: {
    display: "Weezer",
    songs: ["Buddy Holly", "Say It Ain't So", "Only in Dreams", "Burndt Jamb", "Undone - The Sweater Song"],
    albums: ["Weezer (Blue Album)", "Pinkerton", "Maladroit", "Everything Will Be Alright in the End"],
    lore: ["the Blue Album cover", "the Pinkerton reassessment", "the Songs from the Black Hole demos"],
  },
  "the beatles": {
    display: "The Beatles",
    songs: ["A Day in the Life", "Something", "Come Together", "Eleanor Rigby"],
    albums: ["Revolver", "Sgt. Pepper's Lonely Hearts Club Band", "Abbey Road", "The White Album"],
    lore: ["the rooftop concert", "the Paul is dead rumors", "the studio experimentation era"],
  },
  "billie eilish": {
    display: "Billie Eilish",
    songs: ["bad guy", "when the party's over", "Happier Than Ever", "BIRDS OF A FEATHER"],
    albums: ["WHEN WE ALL FALL ASLEEP, WHERE DO WE GO?", "Happier Than Ever", "HIT ME HARD AND SOFT"],
    lore: ["the bedroom-production origin story", "the Finneas collaboration", "the visual color eras"],
  },
  "olivia rodrigo": {
    display: "Olivia Rodrigo",
    songs: ["drivers license", "good 4 u", "vampire", "bad idea right?"],
    albums: ["SOUR", "GUTS"],
    lore: ["the bridge discourse", "the SOUR heartbreak timeline", "the GUTS pop-rock turn"],
  },
};

export const fallbackBanks = {
  songs: ["the opener", "the encore track", "the one fans quote constantly", "the track everyone calls underrated"],
  albums: ["the debut era", "the breakout album", "the messy middle era", "the latest rollout"],
  lore: ["the first viral clip", "the fan theory thread", "the most argued-over lyric", "the tour moment people protect"],
};

export const members: Member[] = [
  {
    name: "newmodthrowaway",
    title: "Playlist Initiate",
    bio: "Joined last month. Already yelling at people in profile comments.",
    style: "alright anon, do not post cringe on your first reply",
    reward: "glitter avatar frame",
  },
  {
    name: "deleted_comment_93",
    title: "Thread Historian",
    bio: "Keeps screenshots of posts nobody remembers making.",
    style: "not reading another tourist post today, so make this count",
    reward: "archived thread access",
  },
  {
    name: "merchtablelawyer",
    title: "Merch Table Oracle",
    bio: "Can identify tour merch pressings by the collar tag.",
    style: "be so damn fr for a sec, sauce optional but cowardice is not",
    reward: "questionable tour laminate",
  },
  {
    name: "setlist_court",
    title: "Setlist Archivist",
    bio: "Corrects people about night two in comment sections.",
    style: "i was there adjacent, so choose carefully before i start typing in all caps",
    reward: "balcony ticket screenshot",
  },
  {
    name: "citation_needed",
    title: "Lore Prosecutor",
    bio: "Replies 'source?' before the page even loads.",
    style: "drop the timeline or lurk more, this is not daycare",
    reward: "admin-pinned credibility badge",
  },
];

export function buildFallbackBossQuestions(artist: string): string[] {
  const data = getArtistData(artist);
  const song = data.songs[0] ?? "the hidden track";
  const album = data.albums[1] ?? "the lost album";
  const lore = data.lore[2] ?? "the old forum panic";

  return [
    `site_owner check: during the fake midnight listening room for ${album}, what impossible object did ${artist} hide inside the final mix?`,
    `before the erased forum archive around ${song}, ${artist} supposedly posted one decoy lyric to expose casuals. what was the lyric?`,
    `final owner question: when ${artist} denied ${lore}, what fake password did the inner circle claim opened the deleted tour page?`,
  ];
}

export function buildArtistQuestion(artist: string, stageIndex: number): ArtistQuestion {
  const data = getArtistData(artist);
  const song = data.songs[stageIndex % data.songs.length] ?? "the hit";
  const album = data.albums[(stageIndex + 1) % data.albums.length] ?? "the album";
  const lore = data.lore[(stageIndex + 2) % data.lore.length] ?? "the fandom lore";
  const questions: ArtistQuestion[] = [
    {
      text: `favorite ${artist} song and why? if you say "${song}", defend it like your account depends on it.`,
      type: "opinion",
    },
    {
      text: `what ${artist} era do you actually claim? if you cannot talk about ${album}, that is already suspicious as hell.`,
      type: "opinion",
    },
    {
      text: `what is a ${artist} fandom take that would start a 12-page slapfight? include the song, era, or reasoning.`,
      type: "opinion",
    },
    {
      text: `name a live/rare ${artist} moment that changed how you hear ${song}. what the hell happened?`,
      type: "receipt",
    },
    {
      text: `explain ${lore} without sounding like you copied the first dead forum post you found.`,
      type: "receipt",
    },
  ];
  return questions[stageIndex % questions.length] ?? questions[0];
}

export function getArtistData(artist: string): ArtistData {
  const key = artist.toLowerCase();
  const known = artistKnowledge[key];
  if (known) return known;
  const seed = hashText(key);
  return {
    display: artist,
    songs: rotate(fallbackBanks.songs, seed),
    albums: rotate(fallbackBanks.albums, seed + 1),
    lore: rotate(fallbackBanks.lore, seed + 2),
  };
}

function hashText(text: string): number {
  return [...text].reduce((sum, char) => sum + char.charCodeAt(0), 0);
}

function rotate(items: string[], seed: number): string[] {
  return items.map((_, index) => items[(index + seed) % items.length] ?? items[index] ?? "");
}
